import { injectable } from 'libs/uikit/ioc';
import { makeAutoObservable, runInAction } from 'mobx';
import uri from 'urijs';
import { appconfig } from '../config/appconfig';
import { BasicUserProfile, Credentials, IAuthenticationStore } from './Abstractions/IAuthenticationStore';
import { simpleSessionStorage, simpleStorage } from './SimpleStorage';
import { userManager } from './UserManager';

@injectable()
export class AuthenticationStore implements IAuthenticationStore {
  currentToken?: string;

  profile?: BasicUserProfile;

  constructor() {
    makeAutoObservable(this);

    this.currentToken = simpleSessionStorage.getItem('token') || simpleStorage.getItem('token');

    this.profile = simpleSessionStorage.getItem('profile') || simpleStorage.getItem('profile');
  }

  get currentTokenExpiry(): Date | undefined {
    if (!this.currentToken) {
      return undefined;
    }
    return parseJwtExpiry(this.currentToken);
  }

  isAuthenticated(): boolean {
    if (!this.currentToken) {
      return false;
    }

    const now = new Date();
    const expiryDate = parseJwtExpiry(this.currentToken);
    return expiryDate !== undefined && now <= expiryDate;
  }

  async login() {
    await userManager.signinRedirect({
      prompt: 'login',
    });
  }

  async loginResourceOwnerPassword(credentials: Credentials) {
    const user = await userManager.signinResourceOwnerCredentials(credentials);
    await this.loginLegacy(user.access_token, false);
  }

  async loginCallback() {
    const user = await userManager.signinRedirectCallback();
    await this.loginLegacy(user.access_token, false);
  }

  async logoutCallback(): Promise<void> {
    await userManager.signoutSilentCallback();
  }

  async loginLegacyResourceOwnerPassword(credentials: Credentials, rememberMe?: boolean) {
    const response = await fetch('/connect/token', {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
      body: new URLSearchParams({
        grant_type: 'password',
        username: credentials.username,
        password: credentials.password,
        client_id: 'app',
        client_secret: 'public',
      }),
    });

    if (response.status === 400) {
      const responseJson = await response.json();
      throw new Error(responseJson.error_description);
    }

    if (response.ok) {
      const responseJson = await response.json();
      await this.loginLegacy(responseJson.access_token, rememberMe);
    } else {
      throw new Error('connection error');
    }
  }

  async loginLegacy(token: string, rememberMe?: boolean) {
    const profile = await this.getProfile(token);

    if (rememberMe) {
      simpleStorage.setItem('token', token);
      simpleStorage.setItem('profile', profile);
    } else {
      simpleSessionStorage.setItem('token', token);
      simpleSessionStorage.setItem('profile', profile);
    }

    runInAction(() => {
      this.currentToken = token;
      this.profile = profile;
    });
  }

  async logout() {
    // The signout error is logged/ignored because it'll always
    // throw if the current login was made via the legacy sigin system.
    // Also, we don't await this promise because we don't want to add a delay
    // to the logout button click.
    userManager
      .signoutSilent()
      .then(() => console.info('idp session signed out'))
      .catch((error) => console.error(error));

    simpleStorage.delete('token');
    simpleStorage.delete('profile');

    simpleSessionStorage.delete('token');
    simpleSessionStorage.delete('profile');

    runInAction(() => {
      this.currentToken = undefined;
      this.profile = undefined;
    });
  }

  private async getProfile(token: string): Promise<BasicUserProfile> {
    const response = await fetch(uri(appconfig.webapi.endpoint).segment('/api/user/getprofile').valueOf(), {
      method: 'post',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return await response.json();
  }
}

function parseJwtExpiry(jwtToken: string): Date | undefined {
  const tokenSegments = jwtToken.split('.');

  // a json web token always has three segments separated by a "."
  // "<header>.<payload>.<signature>"
  // we'll be interested in the <payload> which is a base64
  // encoded JSON object.
  if (tokenSegments.length !== 3) {
    console.error('invalid token format');
    return undefined;
  }

  try {
    // base64 decode the <payload> and then JSON parse it.
    const payload = JSON.parse(atob(tokenSegments[1]));
    if (typeof payload.exp === 'number') {
      // the "payload.exp" field is the expiry date of the token
      // represented as the number of seconds since UTC epoch.
      const expiryDate = new Date(0); // Initialize with epoch time
      expiryDate.setUTCSeconds(payload.exp);
      return expiryDate;
    }
    return undefined;
  } catch (error) {
    console.error('error parsing token:', error);
    return undefined;
  }
}

// (window as any).testSessionExpiry = () => {
//   // expires in 3 seconds
//   const exp = Date.now() / 1000 + 3;

//   const testingToken = ['<header>', JSON.stringify({ exp }), '<signature>']
//     .map((segment) => window.btoa(segment))
//     .join('.');

//   runInAction(() => {
//     authenticationStore.currentToken = testingToken;
//   });
// };
